import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    tasks: [],
    contacts: [],
    categories: [],
    loading: {
      tasks: false,
      contacts: false,
      categories: false,
      calendarTasks: false
    },
    lastFetch: {
      tasks: null,
      contacts: null,
      categories: null
    }
  });

  // Calendar-specific state for monthly task loading
  const [calendarData, setCalendarData] = useState({
    tasksByMonth: {}, // Cache tasks by month: { "2025-8": [...tasks], "2025-9": [...tasks] }
    currentMonthTasks: [],
    loading: false,
    pendingMonth: null // Track which month is currently being loaded
  });

  // Memoized contact name lookup
  const contactMap = useMemo(() => {
    return data.contacts.reduce((map, contact) => {
      map[contact.id] = contact.name || 'Unknown Contact';
      return map;
    }, {});
  }, [data.contacts]);

  const getContactName = useCallback((contactId) => {
    return contactMap[contactId] || 'Unknown Contact';
  }, [contactMap]);

  // Optimized fetch functions
  const fetchTasks = useCallback(async (filters = {}) => {
    try {
      setData(prev => ({ ...prev, loading: { ...prev.loading, tasks: true } }));
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          queryParams.append(key, value);
        }
      });

      const response = await axios.get(`${API}/tasks?${queryParams}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setData(prev => ({
        ...prev,
        tasks: response.data,
        loading: { ...prev.loading, tasks: false }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setData(prev => ({ ...prev, loading: { ...prev.loading, tasks: false } }));
      throw error;
    }
  }, []);

  const fetchContacts = useCallback(async (force = false) => {
    // Check if we already have recent data
    const lastFetch = data.lastFetch?.contacts;
    const now = Date.now();
    const cacheTime = 30000; // 30 seconds cache
    
    if (!force && lastFetch && (now - lastFetch) < cacheTime && data.contacts.length > 0) {
      console.log('Using cached contacts data');
      return data.contacts;
    }

    try {
      setData(prev => ({ ...prev, loading: { ...prev.loading, contacts: true } }));
      
      const response = await axios.get(`${API}/contacts`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setData(prev => ({
        ...prev,
        contacts: response.data,
        loading: { ...prev.loading, contacts: false },
        lastFetch: { ...prev.lastFetch, contacts: now }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setData(prev => ({ ...prev, loading: { ...prev.loading, contacts: false } }));
      throw error;
    }
  }, [data.contacts, data.lastFetch?.contacts]);

  const fetchCategories = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, loading: { ...prev.loading, categories: true } }));
      
      const response = await axios.get(`${API}/categories`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setData(prev => ({
        ...prev,
        categories: response.data,
        loading: { ...prev.loading, categories: false }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      setData(prev => ({ ...prev, loading: { ...prev.loading, categories: false } }));
      throw error;
    }
  }, []);

  // Calendar-specific monthly task fetching with better caching
  const fetchTasksForMonth = useCallback(async (year, month) => {
    const monthKey = `${year}-${month}`;
    
    // Check cache first - if exists, return immediately
    if (calendarData.tasksByMonth[monthKey]) {
      console.log(`✅ Using cached tasks for ${monthKey} (${calendarData.tasksByMonth[monthKey].length} tasks)`);
      setCalendarData(prev => ({
        ...prev,
        currentMonthTasks: prev.tasksByMonth[monthKey],
        loading: false
      }));
      return calendarData.tasksByMonth[monthKey];
    }
    
    // Prevent duplicate API calls for same month
    if (calendarData.loading && calendarData.pendingMonth === monthKey) {
      console.log(`⏳ Already loading ${monthKey}, skipping duplicate request`);
      return [];
    }
    
    try {
      console.log(`📡 Fetching tasks for ${monthKey}...`);
      setCalendarData(prev => ({ 
        ...prev, 
        loading: true,
        pendingMonth: monthKey
      }));
      
      // Calculate start and end dates for the month
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);
      
      // Format dates as ISO strings (date part only for better performance)
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const startTime = performance.now();
      
      const response = await axios.get(`${API}/calendar/tasks?start_date=${startDateStr}&end_date=${endDateStr}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 5000 // 5 second timeout
      });
      
      const monthTasks = response.data;
      const loadTime = Math.round(performance.now() - startTime);
      
      console.log(`✅ Loaded ${monthTasks.length} tasks for ${monthKey} in ${loadTime}ms`);
      
      // Update cache and current month tasks
      setCalendarData(prev => ({
        ...prev,
        tasksByMonth: {
          ...prev.tasksByMonth,
          [monthKey]: monthTasks
        },
        currentMonthTasks: monthTasks,
        loading: false,
        pendingMonth: null
      }));
      
      return monthTasks;
      
    } catch (error) {
      console.error(`❌ Error fetching tasks for month ${monthKey}:`, error);
      setCalendarData(prev => ({ 
        ...prev, 
        loading: false,
        pendingMonth: null
      }));
      throw error;
    }
  }, [calendarData.tasksByMonth, calendarData.loading, calendarData.pendingMonth]);

  // Get cached tasks for a specific month (without fetching)
  const getCachedTasksForMonth = useCallback((year, month) => {
    const monthKey = `${year}-${month}`;
    return calendarData.tasksByMonth[monthKey] || null;
  }, [calendarData.tasksByMonth]);

  // Clear calendar cache (useful when tasks are created/updated/deleted)
  const clearCalendarCache = useCallback(() => {
    setCalendarData({
      tasksByMonth: {},
      currentMonthTasks: [],
      loading: false
    });
  }, []);

  // Background task loading for calendar (loads more tasks after initial load)
  const loadAllTasksInBackground = useCallback(async () => {
    try {
      console.log('📡 Loading all tasks in background for calendar...');
      
      // Don't block - fire and forget approach
      const response = await axios.get(`${API}/tasks?page=1&limit=1000`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 10000 // 10 second timeout
      });
      
      const allTasks = response.data;
      console.log(`✅ Background loaded ${allTasks.length} total tasks`);
      
      // Update the tasks state with all tasks
      setData(prev => ({
        ...prev,
        tasks: allTasks,
        lastFetch: {
          ...prev.lastFetch,
          tasks: Date.now()
        }
      }));
      
      return allTasks;
    } catch (error) {
      console.error('Error loading all tasks in background:', error);
      // Don't throw - this is background loading
      return data.tasks; // Return existing tasks
    }
  }, []);

  // Optimized refresh function
  const refreshData = useCallback(async (types = ['tasks', 'contacts', 'categories']) => {
    const promises = [];
    
    if (types.includes('tasks')) promises.push(fetchTasks());
    if (types.includes('contacts')) promises.push(fetchContacts());
    if (types.includes('categories')) promises.push(fetchCategories());
    
    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  }, [fetchTasks, fetchContacts, fetchCategories]);

  // Contacts are per workspace — reload them whenever the workspace switches.
  const fetchContactsRef = React.useRef(fetchContacts);
  fetchContactsRef.current = fetchContacts;
  useEffect(() => {
    const onChange = () => fetchContactsRef.current(true).catch(() => {});
    window.addEventListener('edotask:workspace-changed', onChange);
    return () => window.removeEventListener('edotask:workspace-changed', onChange);
  }, []);


  // Task operations
  const createTask = useCallback(async (taskData) => {
    try {
      console.log('🟢 [DataContext] Creating task:', taskData);
      const response = await axios.post(`${API}/tasks`, taskData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const newTask = response.data;
      console.log('🟢 [DataContext] New task received from backend:', newTask);
      
      // Optimistic update - add task to the list
      setData(prev => ({
        ...prev,
        tasks: [newTask, ...prev.tasks]
      }));
      
      // Invalidate Components.js cache
      if (window.invalidateTasksCache) {
        console.log('🟢 [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh on next visit
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('🟢 [DataContext] Task created and added to UI successfully');
      return newTask;
    } catch (error) {
      console.error('❌ [DataContext] Error creating task:', error);
      throw error;
    }
  }, []);

  const updateTask = useCallback(async (taskId, taskData) => {
    try {
      console.log('🟡 [DataContext] Updating task:', taskId, taskData);
      const response = await axios.put(`${API}/tasks/${taskId}`, taskData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const updatedTask = response.data;
      console.log('🟡 [DataContext] Updated task received:', updatedTask);
      
      // Optimistic update - replace the task in the list
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(task => 
          task.id === taskId ? updatedTask : task
        )
      }));
      
      // Also update calendar data if the task exists there
      setCalendarData(prev => {
        const updatedTasksByMonth = { ...prev.tasksByMonth };
        Object.keys(updatedTasksByMonth).forEach(monthKey => {
          updatedTasksByMonth[monthKey] = updatedTasksByMonth[monthKey].map(task =>
            task.id === taskId ? updatedTask : task
          );
        });
        
        return {
          ...prev,
          tasksByMonth: updatedTasksByMonth,
          currentMonthTasks: prev.currentMonthTasks.map(task =>
            task.id === taskId ? updatedTask : task
          )
        };
      });
      
      // Invalidate Components.js cache
      if (window.invalidateTasksCache) {
        console.log('🟡 [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('🟡 [DataContext] Task updated in UI successfully');
      return updatedTask;
    } catch (error) {
      console.error('❌ [DataContext] Error updating task:', error);
      throw error;
    }
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    try {
      console.log('🔴 [DataContext] Deleting task:', taskId);
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Optimistic update - remove task from the list
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.filter(task => task.id !== taskId)
      }));
      
      // Also remove from calendar data
      setCalendarData(prev => {
        const updatedTasksByMonth = { ...prev.tasksByMonth };
        Object.keys(updatedTasksByMonth).forEach(monthKey => {
          updatedTasksByMonth[monthKey] = updatedTasksByMonth[monthKey].filter(task =>
            task.id !== taskId
          );
        });
        
        return {
          ...prev,
          tasksByMonth: updatedTasksByMonth,
          currentMonthTasks: prev.currentMonthTasks.filter(task => task.id !== taskId)
        };
      });
      
      // Invalidate Components.js cache
      if (window.invalidateTasksCache) {
        console.log('🔴 [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('🔴 [DataContext] Task deleted from UI successfully');
    } catch (error) {
      console.error('❌ [DataContext] Error deleting task:', error);
      throw error;
    }
  }, []);

  const completeTask = useCallback(async (taskId, completionData) => {
    try {
      console.log('✅ [DataContext] Completing task:', taskId, completionData);
      const response = await axios.post(`${API}/tasks/${taskId}/complete`, completionData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const completedTask = response.data;
      console.log('✅ [DataContext] Task completed:', completedTask);
      
      // Optimistic update - update the task in the list
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(task => 
          task.id === taskId ? completedTask : task
        )
      }));
      
      // Also update calendar data if the task exists there
      setCalendarData(prev => {
        const updatedTasksByMonth = { ...prev.tasksByMonth };
        Object.keys(updatedTasksByMonth).forEach(monthKey => {
          updatedTasksByMonth[monthKey] = updatedTasksByMonth[monthKey].map(task =>
            task.id === taskId ? completedTask : task
          );
        });
        
        return {
          ...prev,
          tasksByMonth: updatedTasksByMonth,
          currentMonthTasks: prev.currentMonthTasks.map(task =>
            task.id === taskId ? completedTask : task
          )
        };
      });
      
      // Invalidate Components.js cache so it refetches on next visit
      if (window.invalidateTasksCache) {
        console.log('✅ [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh on next visit
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('✅ [DataContext] Task completion updated in UI successfully');
      return completedTask;
    } catch (error) {
      console.error('❌ [DataContext] Error completing task:', error);
      throw error;
    }
  }, []);

  // Archive task
  const archiveTask = useCallback(async (taskId) => {
    try {
      console.log('📦 [DataContext] Archiving task:', taskId);
      await axios.post(`${API}/tasks/${taskId}/archive`, {}, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Optimistic update - remove task from main list (it's now archived)
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.filter(task => task.id !== taskId)
      }));
      
      // Also remove from calendar data
      setCalendarData(prev => {
        const updatedTasksByMonth = { ...prev.tasksByMonth };
        Object.keys(updatedTasksByMonth).forEach(monthKey => {
          updatedTasksByMonth[monthKey] = updatedTasksByMonth[monthKey].filter(task =>
            task.id !== taskId
          );
        });
        
        return {
          ...prev,
          tasksByMonth: updatedTasksByMonth,
          currentMonthTasks: prev.currentMonthTasks.filter(task => task.id !== taskId)
        };
      });
      
      // Invalidate Components.js cache
      if (window.invalidateTasksCache) {
        console.log('📦 [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('📦 [DataContext] Task archived and removed from UI successfully');
    } catch (error) {
      console.error('❌ [DataContext] Error archiving task:', error);
      throw error;
    }
  }, []);

  // Restore task from archive
  const restoreTask = useCallback(async (taskIds) => {
    try {
      console.log('♻️ [DataContext] Restoring tasks:', taskIds);
      await axios.post(`${API}/tasks/bulk-restore`, {
        task_ids: Array.isArray(taskIds) ? taskIds : [taskIds]
      }, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Invalidate cache to refresh both archives and main list
      if (window.invalidateTasksCache) {
        console.log('♻️ [DataContext] Invalidating Components.js cache');
        window.invalidateTasksCache();
      }
      
      // Mark that task list needs refresh
      sessionStorage.setItem('needsTaskRefresh', 'true');
      
      console.log('♻️ [DataContext] Task(s) restored successfully');
    } catch (error) {
      console.error('❌ [DataContext] Error restoring task:', error);
      throw error;
    }
  }, []);

  // Contact operations  
  const createContact = useCallback(async (contactData) => {
    try {
      console.log('🔵 Creating contact:', contactData);
      const response = await axios.post(`${API}/contacts`, contactData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const newContact = response.data;
      console.log('🔵 New contact received from backend:', newContact);
      
      // Immediately add the new contact to the UI (optimistic update)
      setData(prev => {
        console.log('🔵 Previous contacts count:', prev.contacts.length);
        const updatedContacts = [newContact, ...prev.contacts];
        console.log('🔵 Updated contacts count:', updatedContacts.length);
        return {
          ...prev,
          contacts: updatedContacts
        };
      });
      
      console.log('🔵 Contact added to UI successfully');
      
      // Invalidate Components.js contacts cache
      if (window.invalidateContactsCache) {
        console.log('🔵 [DataContext] Invalidating Components.js contacts cache');
        window.invalidateContactsCache();
      }
      
      // Mark that contacts list needs refresh on next visit
      sessionStorage.setItem('needsContactsRefresh', 'true');
      
      return newContact;
    } catch (error) {
      console.error('❌ Error creating contact:', error);
      throw error;
    }
  }, []);

  const updateContact = useCallback(async (contactId, contactData) => {
    try {
      console.log('🟡 [DataContext] Updating contact:', contactId, contactData);
      const response = await axios.put(`${API}/contacts/${contactId}`, contactData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const updatedContact = response.data;
      console.log('🟡 [DataContext] Updated contact received:', updatedContact);
      
      // Optimistic update - replace the contact in the list
      setData(prev => ({
        ...prev,
        contacts: prev.contacts.map(contact => 
          contact.id === contactId ? updatedContact : contact
        )
      }));
      
      // Invalidate Components.js contacts cache
      if (window.invalidateContactsCache) {
        console.log('🟡 [DataContext] Invalidating Components.js contacts cache');
        window.invalidateContactsCache();
      }
      
      // Mark that contacts list needs refresh on next visit
      sessionStorage.setItem('needsContactsRefresh', 'true');
      
      console.log('🟡 [DataContext] Contact updated in UI successfully');
      return updatedContact;
    } catch (error) {
      console.error('❌ [DataContext] Error updating contact:', error);
      throw error;
    }
  }, []);

  const deleteContact = useCallback(async (contactId) => {
    try {
      console.log('🔴 [DataContext] Deleting contact:', contactId);
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Optimistic update - remove the contact from the list
      setData(prev => ({
        ...prev,
        contacts: prev.contacts.filter(contact => contact.id !== contactId)
      }));
      
      // Invalidate Components.js contacts cache
      if (window.invalidateContactsCache) {
        console.log('🔴 [DataContext] Invalidating Components.js contacts cache');
        window.invalidateContactsCache();
      }
      
      // Mark that contacts list needs refresh on next visit
      sessionStorage.setItem('needsContactsRefresh', 'true');
      
      console.log('🔴 [DataContext] Contact deleted from UI successfully');
    } catch (error) {
      console.error('❌ [DataContext] Error deleting contact:', error);
      throw error;
    }
  }, []);

  // Category operations
  const createCategory = useCallback(async (categoryData) => {
    try {
      console.log('🟢 [DataContext] Creating category:', categoryData);
      const response = await axios.post(`${API}/categories`, categoryData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const newCategory = response.data;
      console.log('🟢 [DataContext] New category received from backend:', newCategory);
      
      // Immediately add the new category to the UI (optimistic update)
      setData(prev => {
        console.log('🟢 [DataContext] Previous categories count:', prev.categories.length);
        const updatedCategories = [newCategory, ...prev.categories];
        console.log('🟢 [DataContext] Updated categories count:', updatedCategories.length);
        return {
          ...prev,
          categories: updatedCategories
        };
      });
      
      console.log('🟢 [DataContext] Category added to UI successfully');
      
      return newCategory;
    } catch (error) {
      console.error('❌ [DataContext] Error creating category:', error);
      throw error;
    }
  }, []);

  const updateCategory = useCallback(async (categoryId, categoryData) => {
    try {
      console.log('🟡 [DataContext] Updating category:', categoryId, categoryData);
      const response = await axios.put(`${API}/categories/${categoryId}`, categoryData, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const updatedCategory = response.data;
      console.log('🟡 [DataContext] Updated category received:', updatedCategory);
      
      // Optimistic update - replace the category in the list
      setData(prev => ({
        ...prev,
        categories: prev.categories.map(cat => 
          cat.id === categoryId ? updatedCategory : cat
        )
      }));
      
      console.log('🟡 [DataContext] Category updated in UI successfully');
      return updatedCategory;
    } catch (error) {
      console.error('❌ [DataContext] Error updating category:', error);
      throw error;
    }
  }, []);

  const deleteCategory = useCallback(async (categoryId) => {
    try {
      console.log('🔴 [DataContext] Deleting category:', categoryId);
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Optimistic update - remove the category from the list
      setData(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat.id !== categoryId)
      }));
      
      console.log('🔴 [DataContext] Category removed from UI successfully');
    } catch (error) {
      console.error('❌ [DataContext] Error deleting category:', error);
      throw error;
    }
  }, []);

  // Initial data fetch - only fetch when needed
  useEffect(() => {
    // Only fetch the minimal required data on initial load
    // Individual pages will fetch their specific data when needed
    if (data.categories.length === 0 && !data.loading.categories) {
      fetchCategories(); // Categories are small and used across the app
    }
  }, []); // Only run once on mount

  const value = {
    // Data
    ...data,
    
    // Calendar data
    calendarTasks: calendarData.currentMonthTasks,
    calendarLoading: calendarData.loading,
    
    // Helper functions
    getContactName,
    
    // Fetch functions
    fetchTasks,
    fetchContacts,
    fetchCategories,
    refreshData,
    loadAllTasksInBackground,
    
    // Calendar functions
    fetchTasksForMonth,
    getCachedTasksForMonth,
    clearCalendarCache,
    
    // CRUD operations
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    archiveTask,
    restoreTask,
    createContact,
    updateContact,
    deleteContact,
    createCategory,
    updateCategory,
    deleteCategory
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};